import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Button, Flex, FormHelperText, Input, Select, useDisclosure, useToast } from '@chakra-ui/react';
import { deleteAluno, transferirAluno, updateAluno } from '../../services/api';
import { AlunosType } from '.';
import { ProfessoresGet } from '../Professores';
import { CampoForm } from '../../components/CampoForm';
import { ConfirmarExclusao } from '../../components/ConfirmarExclusao';
import { DrawerForm } from '../../components/DrawerForm';
import { ErrosForm, useErrosForm } from '../../hooks/useErrosForm';
import { emailInvalido, vazio } from '../../utils/validacao';
import { CampoDocumento } from '../../components/CampoDocumento';
import { TipoDocumento, erroDocumento } from '../../utils/documento';
import useAuth from '../../hooks/useAuth';

type DrawerCreateProps = {
	isOpen: boolean;
	onOpen: () => void;
	onClose: () => void;
	afterClose: () => void;
	aluno: AlunosType;
	professores: ProfessoresGet[];
};

type Campo = 'nome' | 'sobrenome' | 'documento' | 'email' | 'professorId';

const resumoDasAulas = (quantidade: number) => {
	if (quantidade === 0) return 'Nenhuma aula agendada para transferir.';
	if (quantidade === 1) return '1 aula agendada passou para o novo professor.';
	return `${quantidade} aulas agendadas passaram para o novo professor.`;
};

export function DrawerUpdate({ isOpen, onClose, afterClose, aluno, professores }: DrawerCreateProps) {
	const toast = useToast();
	const confirmacao = useDisclosure();
	const { user } = useAuth();
	const { erros, validar, limparErro, limparTudo } = useErrosForm<Campo>();

	const [ loading, setLoading ] = useState(false);

	const [ nome, setNome ] = useState<string>('');
	const [ sobrenome, setSobrenome ] = useState<string>('');
	const [ documento, setDocumento ] = useState<string>('');
	const [ tipoDocumento, setTipoDocumento ] = useState<TipoDocumento>('CPF');
	const [ email, setEmail ] = useState<string>('');
	const [ professorId, setProfessorId ] = useState<string>('');

	// So ADMIN transfere: a rota do backend recusa PROFESSOR com 403, e a lista
	// de professores so e carregada pelo pai nesse perfil.
	const isAdmin = user?.userType === 'ADMIN';

	// Descarta as edicoes voltando aos dados do aluno, e nao para campos vazios: o
	// pai reaproveita o mesmo objeto ao reabrir a mesma linha, entao limpar aqui
	// deixaria o formulario em branco na proxima abertura.
	const resetForm = useCallback(
		() => {
			setDocumento(aluno.documento);
			setTipoDocumento(aluno.tipoDocumento);
			setEmail(aluno.email);
			setNome(aluno.nome);
			setSobrenome(aluno.sobrenome);
			setProfessorId(aluno.professorId);
			limparTudo();
		},
		[ aluno, limparTudo ]
	);

	useEffect(
		() => {
			if (isOpen) resetForm();
		},
		[ isOpen, resetForm ]
	);

	const montarErros = (): ErrosForm<Campo> => {
		const encontrados: ErrosForm<Campo> = {};

		if (vazio(nome)) encontrados.nome = 'Nome é obrigatório!';
		if (vazio(sobrenome)) encontrados.sobrenome = 'Sobrenome é obrigatório!';

		const erroDoDocumento = erroDocumento(tipoDocumento, documento);
		if (erroDoDocumento) encontrados.documento = erroDoDocumento;

		if (vazio(email)) {
			encontrados.email = 'Email é obrigatório!';
		} else if (emailInvalido(email)) {
			encontrados.email = 'Informe um email válido.';
		}

		if (isAdmin && !professorId) encontrados.professorId = 'Selecione o professor responsável!';

		return encontrados;
	};

	const handleSalvar = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (!validar(montarErros())) return;

		const emailNovo = email.trim();
		const trocouProfessor = isAdmin && professorId !== aluno.professorId;
		// Diferencia as duas etapas no catch: se a transferencia falhar, os dados
		// do cadastro ja foram gravados e o aviso precisa dizer isso.
		let transferindo = false;

		setLoading(true);
		updateAluno(aluno.email, {
			documento: documento.trim(),
			tipoDocumento,
			email: emailNovo,
			nome: nome.trim(),
			sobrenome: sobrenome.trim()
		})
			.then(() => {
				if (!trocouProfessor) return null;
				// A transferencia usa o email ja atualizado: a rota busca o aluno
				// por email, e o update pode ter acabado de troca-lo.
				transferindo = true;
				return transferirAluno(emailNovo, professorId);
			})
			.then((resposta) => {
				setLoading(false);
				toast({
					title: `Aluno(a) atualizado com sucesso.`,
					description: resposta ? resumoDasAulas(resposta.data.aulasTransferidas) : undefined,
					status: 'success',
					isClosable: true
				});
				onClose();
				afterClose();
			})
			.catch((error) => {
				setLoading(false);
				if (!error.response) return;
				const message = error.response.data?.message;
				if (transferindo) {
					// Fecha e recarrega mesmo assim: o cadastro foi salvo, e manter o
					// formulario aberto com os dados antigos convida a um segundo
					// update que erraria pelo email ja trocado.
					toast({
						title: `Dados salvos, mas não foi possível transferir o professor.`,
						description: typeof message === 'string' ? message : undefined,
						status: 'error',
						isClosable: true
					});
					onClose();
					afterClose();
					return;
				}
				toast({
					title: typeof message === 'string' ? message : `Não foi possível atualizar aluno(a).`,
					status: 'error',
					isClosable: true
				});
			});
	};

	const handleExcluir = () => {
		confirmacao.onClose();
		setLoading(true);
		deleteAluno(aluno.email)
			.then(() => {
				setLoading(false);
				toast({
					title: `Aluno(a) removido(a) com sucesso.`,
					status: 'success',
					isClosable: true
				});
				onClose();
				afterClose();
			})
			.catch((error) => {
				setLoading(false);
				if (!error.response) return;
				const message = error.response.data?.message;
				toast({
					title: typeof message === 'string' ? message : `Não foi possível remover aluno(a).`,
					status: 'error',
					isClosable: true
				});
			});
	};

	const handleFechar = () => {
		resetForm();
		onClose();
		afterClose();
	};

	return (
		<>
			<DrawerForm
				isOpen={isOpen}
				onClose={handleFechar}
				titulo="Editar Aluno"
				formId="form-editar-aluno"
				loading={loading}
				onSubmit={handleSalvar}
				acaoDestrutiva={
					<Button colorScheme="red" variant="outline" isDisabled={loading} onClick={confirmacao.onOpen}>
						Excluir
					</Button>
				}
			>
				<Flex gap="16px" flexDir={{ base: 'column', sm: 'row' }}>
					<CampoForm label="Nome" htmlFor="nome" erro={erros.nome}>
						<Input
							id="nome"
							placeholder="Insira o Nome"
							autoComplete="given-name"
							onChange={(e) => {
								setNome(e.target.value);
								limparErro('nome');
							}}
							value={nome}
						/>
					</CampoForm>

					<CampoForm label="Sobrenome" htmlFor="sobrenome" erro={erros.sobrenome}>
						<Input
							id="sobrenome"
							placeholder="Insira o Sobrenome"
							autoComplete="family-name"
							onChange={(e) => {
								setSobrenome(e.target.value);
								limparErro('sobrenome');
							}}
							value={sobrenome}
						/>
					</CampoForm>
				</Flex>

				<CampoDocumento
					tipo={tipoDocumento}
					valor={documento}
					onTipoChange={(novo) => {
						setTipoDocumento(novo);
						limparErro('documento');
					}}
					onValorChange={(valor) => {
						setDocumento(valor);
						limparErro('documento');
					}}
					erro={erros.documento}
				/>

				<CampoForm label="Email" htmlFor="email" erro={erros.email}>
					<Input
						id="email"
						placeholder="aluno@email.com"
						type="email"
						autoComplete="email"
						onChange={(e) => {
							setEmail(e.target.value);
							limparErro('email');
						}}
						value={email}
					/>
				</CampoForm>

				{isAdmin && (
					<CampoForm label="Professor responsável" htmlFor="professor" erro={erros.professorId}>
						<Select
							id="professor"
							placeholder="Selecione o Professor"
							onChange={(e) => {
								setProfessorId(e.target.value);
								limparErro('professorId');
							}}
							value={professorId}
						>
							{professores.map((item) => (
								<option value={item.id} key={item.id} style={{ color: 'black' }}>
									{`${item.nome} ${item.sobrenome}`}
								</option>
							))}
						</Select>
						<FormHelperText>
							Ao trocar o professor, as aulas agendadas e pendentes passam para ele. Aulas em andamento e
							concluídas ficam com o professor atual.
						</FormHelperText>
					</CampoForm>
				)}
			</DrawerForm>

			<ConfirmarExclusao
				isOpen={confirmacao.isOpen}
				onClose={confirmacao.onClose}
				onConfirmar={handleExcluir}
				titulo="Excluir aluno(a)"
				descricao={`Tem certeza que deseja excluir ${aluno.nome} ${aluno.sobrenome}? Esta ação não pode ser desfeita.`}
			/>
		</>
	);
}
